/**
 * Global 401 interceptor — monkey-patches window.fetch so any API response
 * with a 401 auto-clears the cached user and force-redirects to /login.
 *
 * Why: sessions last 30 days but there was no client-side handler for
 * expiry. Returning users (especially on mobile) hit a stale cookie,
 * every fetch quietly failed, and the UI rendered "no data" screens
 * with no clue that they'd been signed out. This restores the expected
 * behaviour: when the session dies, you get punted back to /login with
 * a `next=` param so you land back where you were after signing in.
 *
 * Only intercepts responses from our own API (BACKEND_URL / same-origin
 * /api/…) — third-party fetches (OG previews, external JSON, etc.) go
 * through untouched.
 *
 * Also skips the initial /api/auth/session probe on public pages so an
 * anonymous visitor doesn't get thrown to /login just for arriving.
 */
export function installAuthInterceptor() {
  if (typeof window === 'undefined') return;
  if (window.__sllAuthInterceptorInstalled) return;
  window.__sllAuthInterceptorInstalled = true;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const originalFetch = window.fetch.bind(window);

  // Paths that MAY legitimately return 401 for an anonymous visitor
  // without meaning "you were signed in and got kicked out" — don't
  // redirect on these.
  const anonymousOk = [
    '/api/auth/session',
    '/api/auth/login',
    '/api/auth/register',
    '/api/auth/verify-email',
    '/api/auth/forgot-password',
    '/api/auth/reset-password',
    '/api/auth/2fa',
    '/api/auth/oauth',
  ];

  const authPages = ['/login', '/register', '/reset-password', '/verify', '/auth/'];

  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : (input?.url || '');
    const isOurApi =
      url.startsWith('/api/') ||
      (backendUrl && url.startsWith(backendUrl + '/api/'));

    let response;
    try {
      response = await originalFetch(input, init);
    } catch (err) {
      // Network error — nothing to intercept, let caller handle
      throw err;
    }

    if (!isOurApi || response.status !== 401) return response;

    // Already on an auth page? Don't loop.
    if (authPages.some(p => window.location.pathname.startsWith(p))) return response;

    // Anonymous-safe endpoints don't count.
    if (anonymousOk.some(p => url.includes(p))) return response;

    // Clear cached user & drop the client-side session marker so any
    // downstream code doesn't keep treating them as signed in.
    try {
      sessionStorage.removeItem('sll_user');
      localStorage.removeItem('sll_user');
      sessionStorage.removeItem('session_token');
    } catch { /* ignore storage errors (private mode etc.) */ }

    // Redirect back after login. Preserve the current path + search so
    // members land back where they were.
    const next = window.location.pathname + window.location.search;
    // Guard against redirect loops by using replace.
    window.location.replace(`/login?next=${encodeURIComponent(next)}&expired=1`);

    return response;
  };
}
